import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';

import { useConfigQueries, useUserQuery } from 'modules/App/queries';
import { useExtractServerError } from 'hooks';
import { getGermanDate } from 'utils';

import { useListOneTicketsQuery, useListOneStatusCountsQuery } from './queries';
import {
  useListOneMutation,
  useDeleteListOneOpenPendingTicketMutation,
  useFixAndPromoteTicketMutation,
} from './mutations';

import ListOneUi from './ListOne.ui';

import {
  ListOneFilter,
  ListOneParams,
  PaginatorPageChangeEvent,
  DropdownChangeEvent,
  CalendarChangeEvent,
  ListOneTicket,
} from './ListOne.types';

export default function ListOne() {
  //* Hooks Init
  const toastRef = useRef<Toast>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { extractErrorMessage } = useExtractServerError();

  let params: ListOneParams = {
    page: searchParams.get('page') || '1',
    first: searchParams.get('first') || '0',
    rows: searchParams.get('rows') || '50',
    ticketStatus: searchParams.get('ticketStatus') || 'All',
    ticketType: searchParams.get('ticketType') || 'All',
    employeeId: searchParams.get('employeeId') || '',
    personId: searchParams.get('personId') || '',
    country: searchParams.get('country') || '',
    statusCodes: searchParams.get('statusCodes') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
  };

  //* Local state
  const [filter, setFilter] = useState<ListOneFilter>({
    page: parseInt(params.page),
    first: parseInt(params.first),
    rows: parseInt(params.rows),
    employeeId: params.employeeId,
    personId: params.personId,
    country: params.country,
    ticketStatus: params.ticketStatus,
    ticketType: params.ticketType,
    statusCodes: params.statusCodes,
    dates: [new Date(params.startDate), new Date(params.endDate)],
  });
  const [openPendingTicketId, setOpenPendingTicketId] = useState('');
  const [fixAndPromoteTicket, setFixAndPromoteTicket] = useState<ListOneTicket | null>(null);
  const [documentsTicketId, setDocumentsTicketId] = useState('');
  // Kept as an id, so the panel follows the row through a refetch, and closes
  // if the ticket leaves the list (sent on to List 2).
  const [drawerTicketId, setDrawerTicketId] = useState('');

  //* Queries & Mutations
  const [, endorsementConfig] = useConfigQueries();
  const userQuery = useUserQuery();
  const listOneTicketsQuery = useListOneTicketsQuery(params);
  const statusCounts = useListOneStatusCountsQuery(params);
  const listOneMutation = useListOneMutation({
    onSuccess: handleDownloadSuccess,
  });
  const deleteListOneOpenPendingTicketMutation =
    useDeleteListOneOpenPendingTicketMutation({
      onSuccess: handleDeleteOpenPendingTicketSuccess,
    });
  const fixAndPromoteTicketMutation = useFixAndPromoteTicketMutation({
    onSuccess: handleFixAndPromoteSuccess,
  });

  // A change to one ticket can move it between status tabs, so the counts are
  // fetched again with the list.
  function refetchTickets() {
    listOneTicketsQuery.refetch();
    statusCounts.refetch();
  }

  //* Handlers
  function handleSearchChange(name: string, value: string) {
    setFilter((filter) => ({
      ...filter,
      [name]: value,
    }));
  }

  function handleSearchClick() {
    const employeeId = (filter.employeeId || '').trim();
    const personId = (filter.personId || '').trim();

    const next: any = {
      ...params,
      page: '1',
      rows: '50',
      first: '0',
      employeeId,
      personId,
    };
    if (!employeeId) delete next.employeeId;
    if (!personId) delete next.personId;
    setSearchParams(next);

    setFilter((filter) => ({
      ...filter,
      page: 1,
      rows: 50,
      first: 0,
      employeeId,
      personId,
    }));
  }

  function handleFilterClear() {
    setSearchParams({
      page: '1',
      rows: '50',
      first: '0',
      ticketStatus: 'All',
      ticketType: 'All',
    });

    setFilter({
      page: 1,
      rows: 50,
      first: 0,
      ticketStatus: 'All',
      ticketType: 'All',
      employeeId: '',
      personId: '',
      country: '',
      statusCodes: '',
      dates: null,
    });
  }

  function handleCountryChange(event: DropdownChangeEvent) {
    setSearchParams({
      ...params,
      country: event.value,
      page: '1',
      first: '0',
    });

    setFilter((filter) => ({
      ...filter,
      country: event.value,
      page: 1,
      first: 0,
    }));
  }

  function handleTicketTypeChange(event: DropdownChangeEvent) {
    setSearchParams({
      ...params,
      ticketType: event.value,
      page: '1',
      first: '0',
    });

    setFilter((filter) => ({
      ...filter,
      ticketType: event.value,
      page: 1,
      first: 0,
    }));
  }

  function handleTicketStatusChange(event: DropdownChangeEvent) {
    setSearchParams({
      ...params,
      ticketStatus: event.value,
      page: '1',
      first: '0',
    });

    setFilter((filter) => ({
      ...filter,
      ticketStatus: event.value,
      page: 1,
      first: 0,
    }));
  }

  function handleStatusTabChange(ticketStatus: string) {
    handleTicketStatusChange({ value: ticketStatus } as DropdownChangeEvent);
  }

  function handleStatusCodeChange(event: DropdownChangeEvent) {
    setSearchParams({
      ...params,
      statusCodes: event.value,
      page: '1',
      first: '0',
    });

    setFilter((filter) => ({
      ...filter,
      statusCodes: event.value,
      page: 1,
      first: 0,
    }));
  }

  function handleDateChange(event: CalendarChangeEvent) {
    if (Array.isArray(event.value)) {
      const startDate = !!event.value[0]
        ? getGermanDate(event.value[0].toISOString())
        : '';
      const endDate = !!event.value[1]
        ? getGermanDate(event.value[1].toISOString())
        : '';

      setSearchParams({
        ...params,
        startDate,
        endDate,
      });

      setFilter((filter) => ({
        ...filter,
        dates: event.value,
        page: 1,
        first: 0,
      }));
    }
  }

  function handlePageChange(event: PaginatorPageChangeEvent) {
    setSearchParams({
      ...params,
      page: `${event.page + 1}`,
      first: event.first.toString(),
      rows: event.rows.toString(),
    });

    setFilter((filter) => ({
      ...filter,
      page: event.page,
      first: event.first,
      rows: event.rows,
    }));
  }

  function handleDownloadClick() {
    listOneMutation.mutate({ ...filter, exportable: true });
  }

  function handleDeleteOpenPendingTicketConfirmationDialogVisible(
    ticketId: string
  ) {
    return () => setOpenPendingTicketId(ticketId);
  }

  function handleDeleteOpenPendingTicketConfirmationDialogHide() {
    setOpenPendingTicketId('');
  }

  function handleDeleteOpenPendingTicketClick() {
    deleteListOneOpenPendingTicketMutation.mutate(openPendingTicketId);
  }

  function handleDownloadSuccess({ data }: any) {
    window.open(data.url);
  }

  function handleDeleteOpenPendingTicketSuccess() {
    refetchTickets();
    setOpenPendingTicketId('');
  }

  function handleFixAndPromoteClick(ticket: ListOneTicket) {
    setFixAndPromoteTicket(ticket);
  }

  function handleFixAndPromoteDialogHide() {
    setFixAndPromoteTicket(null);
  }

  function handleFixAndPromoteSubmit(fixData: any) {
    if (!fixAndPromoteTicket) return;
    fixAndPromoteTicketMutation.mutate({
      ticketId: fixAndPromoteTicket._id,
      statusId: fixAndPromoteTicket.statusId,
      fixData,
    });
  }

  function handleDocumentsClick(ticket: ListOneTicket) {
    setDocumentsTicketId(ticket._id);
  }

  function handleTicketOpen(ticket: ListOneTicket) {
    setDrawerTicketId(ticket._id);
  }

  function handleTicketDrawerHide() {
    setDrawerTicketId('');
  }

  function handleDocumentsDialogHide() {
    setDocumentsTicketId('');
  }

  function handleDocumentsApproved(response: any) {
    refetchTickets();
    const data = response?.data;
    toastRef.current?.show({
      severity: data?.movedToList2 ? 'success' : 'warn',
      summary: data?.movedToList2 ? 'Approved and sent' : 'Approved',
      detail: data?.movedToList2
        ? 'The documents are approved and the ticket has been sent on.'
        : `The documents are approved, but the ticket is still waiting: ${
            data?.newStatusDescription ||
            'something else on it needs to be sorted out first'
          }.`,
    });
  }

  function handleFixAndPromoteSuccess(response: any) {
    setFixAndPromoteTicket(null);
    refetchTickets();
    const data = response?.data;
    toastRef.current?.show({
      severity: data?.movedToList2 ? 'success' : 'warn',
      summary: data?.movedToList2 ? 'Promoted' : 'Still in List 1',
      detail: data?.movedToList2
        ? 'Ticket moved to List 2'
        : `New status: ${data?.newStatusDescription || 'Unknown'}`,
    });
  }

  useEffect(() => {
    if (
      listOneTicketsQuery.isError ||
      listOneMutation.isError ||
      deleteListOneOpenPendingTicketMutation.isError ||
      fixAndPromoteTicketMutation.isError
    ) {
      const errorMessage = extractErrorMessage(
        listOneTicketsQuery.error ||
          listOneMutation.error ||
          deleteListOneOpenPendingTicketMutation.error ||
          fixAndPromoteTicketMutation.error
      );
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
      });
    }
  }, [
    listOneTicketsQuery.isError,
    listOneMutation.isError,
    deleteListOneOpenPendingTicketMutation.isError,
    fixAndPromoteTicketMutation.isError,
  ]);

  const options = {
    ticketStatuses:
      endorsementConfig.data?.data.statuses.map(({ key, value }) => {
        if (key === 'All') {
          return {
            key,
            label: 'All statuses',
            value: key,
          };
        }
        return {
          key: value,
          label: key,
          value,
        };
      }) || [],
    ticketTypes:
      endorsementConfig.data?.data.ticketsType.map(({ key, value }) => {
        if (key === 'All') {
          return {
            key,
            label: 'All types',
            value: key,
          };
        }
        return {
          key: value,
          label: key,
          value,
        };
      }) || [],
    countries:
      endorsementConfig.data?.data.countries.map(({ _id, value }) => ({
        key: _id,
        label: value,
        value,
      })) || [],
    statusCodes:
      endorsementConfig.data?.data.statusCodes.map(
        ({ _id, statusId, statusDescription }) => {
          return {
            key: _id,
            label: statusDescription,
            value: statusId.toString(),
          };
        }
      ) || [],
  };

  const tickets = listOneTicketsQuery.data?.data.data || [];

  const data = {
    tickets,
    drawerTicket: tickets.find(({ _id }) => _id === drawerTicketId) || null,
  };

  const handlers = {
    handleTicketStatusChange,
    handleStatusTabChange,
    handleTicketTypeChange,
    handleCountryChange,
    handleDateChange,
    handlePageChange,
    handleFilterClear,
    handleSearchChange,
    handleSearchClick,
    handleDownloadClick,
    handleDeleteOpenPendingTicketClick,
    handleStatusCodeChange,
    handleDeleteOpenPendingTicketConfirmationDialogVisible,
    handleDeleteOpenPendingTicketConfirmationDialogHide,
    handleFixAndPromoteClick,
    handleFixAndPromoteDialogHide,
    handleFixAndPromoteSubmit,
    handleDocumentsClick,
    handleDocumentsDialogHide,
    handleTicketOpen,
    handleTicketDrawerHide,
    handleDocumentsApproved,
  };

  return (
    <>
      <Toast ref={toastRef} />

      <ListOneUi
        isLoading={listOneTicketsQuery.isLoading}
        isExporting={listOneMutation.isLoading}
        isFixAndPromoteDialogVisible={fixAndPromoteTicket !== null}
        fixAndPromoteTicket={fixAndPromoteTicket}
        isFixAndPromoteLoading={fixAndPromoteTicketMutation.isLoading}
        documentsTicketId={documentsTicketId}
        isDeleteOpenPendingTicketButtonVisible={
          !!userQuery.data?.data.data.user.userTypes
            .find(
              (type) => type.id === userQuery.data?.data.data.user.userTypeId
            )
            ?.buttons.find(
              (button) => button.identifier === 'deleteOpenPendingTicket'
            ) ?? false
        }
        openPendingTicketId={openPendingTicketId}
        totalRecords={listOneTicketsQuery.data?.data.total || 0}
        statusCounts={statusCounts.counts}
        options={options}
        filter={filter}
        data={data}
        handlers={handlers}
      />
    </>
  );
}
